import saveAs from "file-saver";
import { computed } from "mobx";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { usePipedriveStore } from "..";
import type IDeal from "../interfaces/IDeal";
import ILoiModel from "../interfaces/ILoiModel";
import type IProperty from "../interfaces/IProperty";
import ReportsApiService from "../services/reportsApiService";

export function useLoiGenerator(deal: IDeal, property: IProperty | undefined) {
    const pipedriveStore = usePipedriveStore();
    const reportsApiService = useMemo(() => new ReportsApiService(), []);
    const contactPipedrivePerson = useMemo(() => computed(() => pipedriveStore.persons.find(p => p.id === deal.contactPersonId)), [deal]).get();
    const contactPipedriveOrganization = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.id === deal.contactCompanyId)), [deal]).get();
    const brokerPipedrivePerson = useMemo(() => computed(() => pipedriveStore.persons.find(p => p.id === deal.brokerId)), [deal]).get();
    const brokerPipedriveOrganization = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.id === deal.brokerId)), [deal]).get();
    const [isGenerating, setIsGenerating] = useState(false);

    const generateLoi = useCallback(async () => {
        if (!property) return;
        try {
            setIsGenerating(true);

            const model: ILoiModel = {
                contactName: contactPipedrivePerson?.name ?? null,
                contactCompany: contactPipedriveOrganization?.name ?? null,
                brokerName: brokerPipedrivePerson?.name ?? null,
                brokerCompany: brokerPipedriveOrganization?.name ?? null,
                address: deal.address,
                addressState: property.state,
                ownerEntity: property.ownerEntity,
                landSize: deal.landSizeAcres,
                buildingSize: deal.buildingSize,
                dealValue: deal.value,
            };

            const file = await reportsApiService.loi(model);
            saveAs(file.data, file.name);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : String(err));
        } finally {
            setIsGenerating(false);
        }
    }, [deal, property, pipedriveStore, reportsApiService]);

    return useMemo(() => ({ generateLoi, isGenerating }), [generateLoi, isGenerating]);
}
